import { collectEntityTargetExtentPoints } from '@toolproof-core/visualization/rendering/_pure/frame-bounds';
import { resolveGuidanceItems } from '@toolproof-core/visualization/runtime/_pure/visual-guidance';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { VisualGuidance, VisualTarget } from '@toolproof-core/visualization/_contract/guidance';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import { selectTraceEpisodePresentation } from '@/_pure/episodePresentation';
import { resolveWorkflowScenePlan } from './episodeCatalog';
import { buildToolStepItemKey, parseWorkflowStateEntryItemKey } from '@/workflow/_pure/keyCodec';
import { teachingFeaturePaths } from './teachingFeatures';
import type { WorkflowTraceTarget } from './_contract/episode';
import type { RuntimeState } from './_contract/runtime';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from './_contract/config';
import type { TraceSnapshotView } from './_contract/traceSnapshot';
import { buildRuntimeToBranchToolStepKey } from './_pure/model/traceSnapshotBuilder';

function resolveTarget(frame: SpaceDerivationFrame, snapshot: TraceSnapshotView, target: WorkflowTraceTarget): VisualTarget[] {
    if (target.targetKind === 'item' || target.targetKind === 'constellation') return [target];
    if (target.targetKind === 'flag' || target.targetKind === 'connector' || target.targetKind === 'dependency') return [];
    if (target.targetKind === 'workflow') return snapshot.position.orderedToolSteps.map(step => ({
        targetKind: 'item', itemKey: buildToolStepItemKey(step.stepKey),
    }));
    if (target.targetKind === 'branch-case') {
        const branch = snapshot.macroStepControl.branch.authored;
        if (new Set(branch.branchOwnerByToolStepKey.values()).size !== 1) {
            throw new Error('Workflow case teaching requires exactly one BranchStep.');
        }
        const steps = snapshot.position.orderedToolSteps.filter(step =>
            branch.branchCaseIndexByToolStepKey.get(step.stepKey) === target.caseIndex
            && (!target.slot || (target.slot === 'when' ? branch.branchWhenToolStepKeys : branch.branchWhatToolStepKeys).has(step.stepKey)));
        if (!steps.length) throw new Error(`Workflow BranchStep case ${target.caseIndex}/${target.slot ?? 'both'} is absent.`);
        return steps.map(step => ({ targetKind: 'item', itemKey: buildToolStepItemKey(step.stepKey) }));
    }
    const execution = snapshot.tickTracePoints.filter(point => point.tickKind === 'tool_execution')[target.execution - 1];
    if (!execution) throw new Error(`Workflow teaching execution ${target.execution} is absent.`);
    const executionKey = execution.stepKey;
    if (!isToolStepKey(executionKey)) throw new Error('Workflow teaching execution must identify a ToolStep.');
    const branchKeys = buildRuntimeToBranchToolStepKey({ tickTracePoints: snapshot.tickTracePoints,
        appliedTickCount: snapshot.tickTracePoints.length,
        branchToolStepKeyByAddress: snapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress });
    const stepKey = branchKeys.get(executionKey) ?? executionKey;
    const step = snapshot.position.orderedToolSteps.find(step => step.stepKey === stepKey);
    if (!step) throw new Error(`Workflow teaching ToolStep ${target.execution} is absent in ${snapshot.selectedTraceHandle} at ${snapshot.position.appliedReplayPositionIndex}.`);
    if (target.targetKind === 'tool-step') return [{ targetKind: 'item', itemKey: buildToolStepItemKey(step.stepKey) }];
    const items = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'entity' ? collection.items : []);
    const entry = items.find(item => {
        const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
        return parsed?.stepKey === stepKey && parsed.direction === target.direction && parsed.roleName === target.roleName;
    });
    if (!entry) throw new Error(`Workflow teaching entry ${target.execution}/${target.roleName} is absent.`);
    return [{ targetKind: 'item', itemKey: entry.itemKey }];
}
function isToolStepKey(key: string): key is `TOOL_STEP-${string}` {
    return key.startsWith('TOOL_STEP-');
}

export function deriveTraceTeaching(frame: SpaceDerivationFrame, state: RuntimeState, data: CosmosDataModel,
    config: Config, snapshot?: TraceSnapshotView) {
    if (!state.episode || state.conceptArrangement || state.pendingReplayMove || state.episode.error) return undefined;
    const { scene, plan, replay, openingStepIndex } = resolveWorkflowScenePlan(data, state, state.episode.episodeHandle);
    const beat = scene.beats[state.episode.beatIndex];
    if (!beat?.presentation) return undefined;
    if (!snapshot) throw new Error('Workflow trace teaching requires a snapshot.');
    const selection = selectTraceEpisodePresentation({ plan, replay, playback: state.episode,
        committedStepIndex: snapshot.position.appliedReplayPositionIndex, openingStepIndex });
    if (!selection) return undefined;
    const segment = selection.selected;
    const color = config.domainPresentation.entities.tools.item?.visualStyle?.activeHighlightColor ?? '#ffcc33';
    const guidance: VisualGuidance = { key: `${state.episode.episodeHandle}:${state.sceneIndex}:${state.episode.beatIndex}:${state.episode.revision}:${segment.id}`,
        emphases: [], pathContours: [], pointerPlacement: segment.pointerPlacement ?? { focus: 'targets', depth: 0.2, lift: 1.2 } };
    const emphases: NonNullable<VisualGuidance['emphases']>[number][] = [];
    const paths: NonNullable<VisualGuidance['pathContours']>[number][] = [];
    const flags: NonNullable<VisualGuidance['flagContours']>[number][] = [];
    const points: Vector3Tuple[] = [];
    for (const effect of segment.effects) {
        if (effect.effectKind !== 'emphasis') throw new Error('Unsupported Workflow teaching effect.');
        const target = effect.target;
        if (target.targetKind === 'flag' || target.targetKind === 'connector') {
            const features = teachingFeaturePaths(frame, target, [0, 0.28, 1]);
            if (target.targetKind === 'flag') flags.push({ key: `flag:${target.itemKey}`, itemKey: target.itemKey, color });
            else paths.push(...features.map((points, index) => ({ key: `connector:${target.itemKey}:${index}`, points, color })));
            points.push(...features.flat());
        } else if (target.targetKind === 'dependency') {
            const [entry] = resolveTarget(frame, snapshot, { targetKind: 'entry', execution: target.execution,
                direction: 'input', roleName: target.roleName });
            const connectors = frame.itemCollections.flatMap(collection => collection.itemCollectionKind === 'connector' ? collection.items : [])
                .filter(item => item.endpoints.from.endpointKind === 'static'
                    && item.endpoints.from.itemKey === (entry?.targetKind === 'item' ? entry.itemKey : undefined)
                    && item.itemKey.includes('TYPE-WorkflowStateEntry'));
            if (!connectors.length) throw new Error(`Workflow teaching dependency ${target.execution}/${target.roleName} is absent.`);
            for (const connector of connectors) {
                const features = teachingFeaturePaths(frame, { targetKind: 'connector', itemKey: connector.itemKey });
                paths.push(...features.map((points, index) => ({ key: `${connector.itemKey}:${index}`, points, color })));
                points.push(...features.flat());
            }
        } else {
            const targets = resolveTarget(frame, snapshot, target);
            const keys = resolveGuidanceItems(frame, targets);
            const pointer = segment.cameraView !== 'detail' && effect.marks?.includes('pointer');
            emphases.push({ targets, color, marks: pointer ? ['contour', 'pointer'] : ['contour'],
                pointerAnchor: 'flag-or-item', pointerGapPixels: 4, ...(pointer && effect.caption ? { caption: effect.caption } : {}) });
            points.push(...collectEntityTargetExtentPoints(frame, new Set(keys)));
        }
    }
    if (!points.length) throw new Error('Workflow trace teaching presentation has no visible subject.');
    return { guidance: { ...guidance, emphases, pathContours: paths, flagContours: flags }, points };
}

export function withTraceTeachingCamera(frame: SpaceDerivationFrame, teaching: ReturnType<typeof deriveTraceTeaching>): SpaceDerivationFrame {
    if (!teaching) return frame;
    const camera = frame.spaceRenderOptions?.camera;
    if (!camera?.graph) throw new Error('Workflow teaching camera requires a camera graph.');
    const min: Vector3Tuple = [Infinity, Infinity, Infinity], max: Vector3Tuple = [-Infinity, -Infinity, -Infinity];
    for (const point of teaching.points) for (let axis = 0; axis < 3; axis++) {
        min[axis] = Math.min(min[axis]!, point[axis]!);
        max[axis] = Math.max(max[axis]!, point[axis]!);
    }
    const center: Vector3Tuple = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const view = `teaching:${teaching.guidance.key}`;
    return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, camera: { ...camera,
        activeView: view, navigationMode: 'guided', navigationDurationMs: 1100, navigationOrbitCenter: null,
        graph: { ...camera.graph,
            stations: { ...camera.graph.stations, [view]: { stationKind: 'framing',
                framing: { framingKind: 'extent', bounds: { center, points: teaching.points }, direction: [0, 0.28, 1], padding: 1.8 }, fov: 55 } },
            views: { ...camera.graph.views, [view]: { station: view, target: { anchorKind: 'position', position: center } } },
            paths: [...camera.graph.paths, ...Object.keys(camera.graph.views).map(from => ({
                pathId: `${from}->${view}`, from, to: view, pathKind: 'straight' as const,
            }))],
        } } } };
}
